import { useState } from 'react';
import { ChevronLeft, ChevronRight, LayoutDashboard, BarChart3, Users, FileText, User, ClipboardCheck } from 'lucide-react';

export type DashboardTab =
  | 'Dashboard'
  | 'Analytics'
  | 'People'
  | 'Reports'
  | 'Take a Test'
  | 'Profile & Access';

interface NavigationProps {
  activeTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
  className?: string;
  sticky?: boolean;
  enableCollapse?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const tabs: { name: DashboardTab; icon: React.ElementType }[] = [
  { name: 'Take a Test', icon: ClipboardCheck },
  { name: 'Dashboard', icon: LayoutDashboard },
  { name: 'Analytics', icon: BarChart3 },
  { name: 'People', icon: Users },
  { name: 'Reports', icon: FileText },
  { name: 'Profile & Access', icon: User },
];

export function Navigation({
  activeTab,
  onTabChange,
  className = '',
  sticky = true,
  enableCollapse = true,
  collapsed,
  onCollapsedChange,
}: NavigationProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const resolvedCollapsed = collapsed ?? isCollapsed;
  const showCollapsed = enableCollapse && resolvedCollapsed;

  const handleToggle = () => {
    const next = !resolvedCollapsed;
    if (onCollapsedChange) {
      onCollapsedChange(next);
    }
    if (collapsed === undefined) {
      setIsCollapsed(next);
    }
  };

  return (
    <nav
      aria-label="Admin sections"
      className={`bg-navy-800 border border-navy-700 rounded-2xl p-6 h-fit transition-all duration-300 ${
        sticky ? 'sticky top-8' : ''
      } ${enableCollapse ? (resolvedCollapsed ? 'w-20' : 'w-64') : 'w-full'} ${className}`}
    >
      <div className={`flex items-center mb-6 ${showCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!showCollapsed && <h2 className="text-lg font-bold text-white">Navigation</h2>}
        {enableCollapse && (
          <button
            onClick={handleToggle}
            className={`p-2 hover:bg-navy-700 rounded-lg transition-colors ${showCollapsed ? '' : 'ml-auto'}`}
            aria-label={showCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {showCollapsed ? (
              <ChevronRight size={20} className="text-navy-200" />
            ) : (
              <ChevronLeft size={20} className="text-navy-200" />
            )}
          </button>
        )}
      </div>
      <div className="space-y-2">
        {tabs.map(({ name, icon: Icon }) => (
          <button
            key={name}
            type="button"
            onClick={() => onTabChange(name)}
            aria-current={activeTab === name ? 'page' : undefined}
            className={`
              w-full rounded-lg text-sm font-medium transition-all
              ${activeTab === name ? 'bg-navy-700 text-gold-400' : 'text-navy-200 hover:bg-navy-900 hover:text-white'}
              ${showCollapsed ? 'flex items-center justify-center p-3' : 'text-left flex items-center gap-3 px-4 py-3'}
            `}
            title={showCollapsed ? name : undefined}
          >
            <Icon size={20} className="flex-shrink-0" />
            {!showCollapsed && <span>{name}</span>}
          </button>
        ))}
      </div>
    </nav>
  );
}
